import type { Router } from 'vue-router';

const CHUNK_RELOAD_TS_KEY = 'aipd_chunk_reload_ts';

/** 部署后旧入口仍引用已删除的 hash chunk 时，动态 import 会失败 */
export function isChunkLoadError(err: unknown): boolean {
  const parts: string[] = [];
  const push = (v: unknown) => {
    if (v == null) return;
    if (v instanceof Error) {
      parts.push(v.message, v.name);
      if (v.cause) push(v.cause);
      return;
    }
    parts.push(String(v));
  };
  push(err);
  if (err && typeof err === 'object') {
    const o = err as Record<string, unknown>;
    push(o.message);
    push(o.reason);
    push(o.cause);
  }
  const msg = parts.join(' ');
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Unable to preload CSS/i.test(msg) ||
    /Loading chunk [\w-]+ failed/i.test(msg)
  );
}

/**
 * 强制整页跳转到目标路由，带时间戳冲掉中间层对 HTML 的缓存，拉取最新 index.html。
 * 15s 内只触发一次，避免死循环刷新。
 */
export function recoverFromChunkLoadError(targetUrl?: string): boolean {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_TS_KEY) || '0');
  if (last && now - last < 15_000) {
    return false;
  }
  sessionStorage.setItem(CHUNK_RELOAD_TS_KEY, String(now));

  const raw =
    (targetUrl && targetUrl.trim()) ||
    `${window.location.pathname}${window.location.search}${window.location.hash}` ||
    '/';
  try {
    const url = new URL(raw, window.location.origin);
    url.searchParams.set('_chunk_reload', String(now));
    window.location.replace(`${url.pathname}${url.search}${url.hash}`);
  } catch {
    window.location.reload();
  }
  return true;
}

/** 路由懒加载 chunk 404 / 缓存过期时自动刷新一次以拉取新 index.html */
export function setupChunkLoadRecovery(router: Router): void {
  router.onError((error, to) => {
    if (!isChunkLoadError(error)) return;
    recoverFromChunkLoadError(to.fullPath);
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLoadError(event.reason)) return;
    if (recoverFromChunkLoadError()) {
      event.preventDefault();
    }
  });

  // Vite 预加载失败（部署换 hash 后常见）
  window.addEventListener('vite:preloadError', ((event: Event) => {
    const pe = event as Event & { payload?: unknown; preventDefault?: () => void };
    pe.preventDefault?.();
    recoverFromChunkLoadError();
  }) as EventListener);
}
